import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import React from "react";

interface Design {
    id: string;
    panelNumber: string;
    designName: string;
    status: string;
    designer: string;
    lastModified: string;
    progress: number;
}

interface Template {
    id: string;
    name: string;
    category: string;
    description: string;
}

interface Panel {
    id: string;
    panelNumber: string;
    panelType: string;
    dimensions: string;
    designStatus: string;
    designer: string;
    productionStatus: string;
    priority: string;
    scheduledDate: string;
}

export default class PanelDesignCenterStore {
    // Observables
    isLoading = false;
    error: string | null = null;
    designs: Design[] = [];
    currentDesign: Design | null = null;
    currentPanel: Panel | null = null;
    templates: Template[] = [];
    activeTab = "2d";

    // Constructor
    constructor(private rootStore: RootStore) {
        makeAutoObservable(this);
    }

    // Actions
    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    setActiveTab(tab: string) {
        this.activeTab = tab;
    }

    // Initialize design center with job and panel data
    async initializeDesignCenter(jobId?: string, panelId?: string) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Load designs for the job
            await this.loadDesigns(jobId);

            // Load templates
            await this.loadTemplates();

            // Load panel data if panelId is provided
            if (panelId) {
                await this.loadPanelData(panelId);
            }

        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to initialize design center");
            });
            console.error("Failed to initialize design center:", error);
        } finally {
            this.setLoading(false);
        }
    }

    // Load panel data
    async loadPanelData(panelId: string) {
        try {
            // Mock panel data - replace with actual API call
            const mockPanel: Panel = {
                id: panelId,
                panelNumber: "P-001",
                panelType: "Wall Panel",
                dimensions: "8' x 10' x 6\"",
                designStatus: "In Progress",
                designer: "Alice Johnson",
                productionStatus: "Design Pending",
                priority: "High",
                scheduledDate: "2024-07-15",
            };

            runInAction(() => {
                this.currentPanel = mockPanel;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to load panel data");
            });
            console.error("Failed to load panel data:", error);
        }
    }

    // Export design
    async exportDesign() {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock export operation
            console.log("Exporting design...");

            // Simulate export delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            // In a real implementation, this would generate and download files
            alert("Design exported successfully!");

        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to export design");
            });
            console.error("Failed to export design:", error);
        } finally {
            this.setLoading(false);
        }
    }

    // Render properties panel
    renderPropertiesPanel() {
        if (!this.currentPanel) {
            return React.createElement('div', {}, 'No panel selected');
        }

        return React.createElement('div', { style: { padding: '12px' } }, [
            React.createElement('h4', { key: 'title' }, 'Panel Properties'),
            React.createElement('div', { key: 'props' }, [
                React.createElement('p', { key: 'number' }, `Panel: ${this.currentPanel.panelNumber}`),
                React.createElement('p', { key: 'type' }, `Type: ${this.currentPanel.panelType}`),
                React.createElement('p', { key: 'dimensions' }, `Dimensions: ${this.currentPanel.dimensions}`),
                React.createElement('p', { key: 'status' }, `Status: ${this.currentPanel.designStatus}`),
                React.createElement('p', { key: 'designer' }, `Designer: ${this.currentPanel.designer}`),
            ])
        ]);
    }

    // Render layers panel
    renderLayersPanel() {
        return React.createElement('div', { style: { padding: '12px' } }, [
            React.createElement('h4', { key: 'title' }, 'Design Layers'),
            React.createElement('div', { key: 'layers' }, [
                React.createElement('div', { key: 'layer1', style: { padding: '4px', borderBottom: '1px solid #eee' } }, 'Base Layer'),
                React.createElement('div', { key: 'layer2', style: { padding: '4px', borderBottom: '1px solid #eee' } }, 'Reinforcement'),
                React.createElement('div', { key: 'layer3', style: { padding: '4px', borderBottom: '1px solid #eee' } }, 'Connections'),
                React.createElement('div', { key: 'layer4', style: { padding: '4px', borderBottom: '1px solid #eee' } }, 'Annotations'),
            ])
        ]);
    }

    // Render history panel
    renderHistoryPanel() {
        return React.createElement('div', { style: { padding: '12px' } }, [
            React.createElement('h4', { key: 'title' }, 'Design History'),
            React.createElement('div', { key: 'history' }, [
                React.createElement('div', { key: 'h1', style: { padding: '4px', fontSize: '12px', borderBottom: '1px solid #eee' } }, 'Created design - Today'),
                React.createElement('div', { key: 'h2', style: { padding: '4px', fontSize: '12px', borderBottom: '1px solid #eee' } }, 'Modified dimensions - Yesterday'),
                React.createElement('div', { key: 'h3', style: { padding: '4px', fontSize: '12px', borderBottom: '1px solid #eee' } }, 'Added reinforcement - 2 days ago'),
            ])
        ]);
    }

    // Async actions
    async loadDesigns(jobId?: string) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock data for now
            const mockDesigns = [
                {
                    id: "design-1",
                    panelNumber: "P-001",
                    designName: "Wall Panel Design A",
                    status: "In Progress",
                    designer: "Alice Johnson",
                    lastModified: "2024-07-06",
                    progress: 75,
                },
                {
                    id: "design-2",
                    panelNumber: "P-002",
                    designName: "Floor Panel Design B",
                    status: "Under Review",
                    designer: "Bob Wilson",
                    lastModified: "2024-07-05",
                    progress: 90,
                },
            ];

            runInAction(() => {
                this.designs = mockDesigns;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to load designs");
            });
            console.error("Failed to load designs:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async loadTemplates() {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock data for now
            const mockTemplates = [
                {
                    id: "template-1",
                    name: "Standard Wall Panel",
                    category: "Wall",
                    description: "Standard precast wall panel template",
                },
                {
                    id: "template-2",
                    name: "Standard Floor Panel",
                    category: "Floor",
                    description: "Standard precast floor panel template",
                },
            ];

            runInAction(() => {
                this.templates = mockTemplates;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to load templates");
            });
            console.error("Failed to load templates:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async saveDesign(designData: any) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock save operation
            console.log("Saving design:", designData);

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            runInAction(() => {
                if (designData.id) {
                    // Update existing design
                    const index = this.designs.findIndex(d => d.id === designData.id);
                    if (index >= 0) {
                        this.designs[index] = { ...this.designs[index], ...designData };
                    }
                } else {
                    // Add new design
                    const newDesign = {
                        ...designData,
                        id: `design-${Date.now()}`,
                        lastModified: new Date().toISOString().split('T')[0],
                    };
                    this.designs.push(newDesign);
                }
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to save design");
            });
            console.error("Failed to save design:", error);
        } finally {
            this.setLoading(false);
        }
    }

    // Computed values
    get hasDesigns() {
        return this.designs.length > 0;
    }

    get designsInProgress() {
        return this.designs.filter(design => design.status === "In Progress");
    }

    get designsUnderReview() {
        return this.designs.filter(design => design.status === "Under Review");
    }
}
